/**
 * Work the reload registry cannot see, read from the page itself (#7687).
 * Any of these keeps a hidden tab from counting as idle for a build reload:
 * audible media that is still playing (no input events while a video runs
 * in the background), an open dialog, or a focused text field holding typed
 * text that no registry owner tracks (question cards, dialog forms). Muted
 * autoplay loops (asset tiles, attachment rails) are decoration, not work.
 */
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'number',
  'password',
]);

export function hasUnregisteredPageActivity(doc: Document): boolean {
  for (const media of doc.querySelectorAll('video, audio')) {
    if (
      media instanceof HTMLMediaElement &&
      !media.paused &&
      !media.ended &&
      !media.muted &&
      media.volume > 0
    )
      return true;
  }
  // Anything in fullscreen, a video in picture-in-picture, or a Document
  // Picture-in-Picture window (the YouTube player) is being watched.
  const pip = (doc as Document & { pictureInPictureElement?: Element | null })
    .pictureInPictureElement;
  const view = doc.defaultView as
    (Window & { documentPictureInPicture?: { window?: unknown } }) | null;
  if (
    doc.fullscreenElement != null ||
    pip instanceof HTMLMediaElement ||
    view?.documentPictureInPicture?.window != null
  )
    return true;
  if (doc.querySelector('[role="dialog"], [role="alertdialog"], dialog[open]'))
    return true;
  const focused = doc.activeElement;
  if (
    focused instanceof HTMLTextAreaElement ||
    (focused instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(focused.type))
  )
    return focused.value.trim() !== '';
  if (focused instanceof HTMLElement && focused.isContentEditable)
    return (focused.textContent ?? '').trim() !== '';
  return false;
}
